/**
 * Togs "Show more" infinite scroll — every collection page.
 *
 * Appends the next paginated page into the existing product grid instead of
 * navigating to it. Each request uses Symmetry's `?section_id=` rendering, so
 * the response is just the collection section, not a whole page.
 *
 * Activated by the `[data-togs-infinite-scroll]` element rendered in
 * sections/main-collection.liquid, which outputs whenever that section's
 * "Show more instead of page numbers" setting is on (the default). With
 * JavaScript off nothing here runs and the numbered pagination links, which
 * are still in the DOM, keep working.
 */
(function () {
  'use strict';

  // Do NOT rewrite the address bar to ?page=N as pages load.
  //
  // It looks like it helps — a refresh lands near where you left off — but the
  // server renders that page *alone*, so refreshing on ?page=8 leaves the
  // shopper on the last 24 products with no route back to the start. Leaving
  // the URL alone also means Back from a product page usually restores the
  // whole scrolled grid from the browser's back/forward cache, which is the
  // behaviour we wanted in the first place.
  //
  // Anyone who still arrives on a ?page=N URL (an old link, a search result)
  // gets the "Load previous" control rendered by main-collection.liquid.
  var UPDATE_URL = false;

  // How far ahead of the sentinel entering the viewport we start fetching.
  var PREFETCH_MARGIN = '800px 0px';

  var ui = document.querySelector('[data-togs-infinite-scroll]');
  if (!ui || !('IntersectionObserver' in window)) return;

  var section = ui.closest('[data-section-id]');
  if (!section) return;

  var listing = section.querySelector('.collection-listing');
  var paginationRow = section.querySelector('.pagination-row');
  if (!listing || !paginationRow) return;

  var moreBtn = ui.querySelector('.togs-inf__more');
  var status = ui.querySelector('.togs-inf__status');
  // Pages loaded silently before the button appears, and pages bought by each
  // press of it. Both are page counts, so at 24 per page: 3 => 72 more products
  // on top of the 24 rendered by the server, 4 => 96 per click.
  var autoPages = parseInt(ui.dataset.autoPages, 10) || 3;
  var clickPages = parseInt(ui.dataset.clickPages, 10) || autoPages;
  var sectionId = section.dataset.sectionId;

  // The current budget: autoPages to begin with, clickPages after a press.
  var budget = autoPages;

  var prevUi = section.querySelector('[data-togs-infinite-prev]');
  var prevBtn = prevUi && prevUi.querySelector('.togs-inf__prev');

  var nextUrl = null;
  var prevUrl = null;
  var autoLoaded = 0;
  var loading = false;
  var controller = null;
  var sentinelVisible = false;

  // Sentinel sits between the grid and the load-more button.
  var sentinel = document.createElement('div');
  sentinel.className = 'togs-inf__sentinel';
  sentinel.setAttribute('aria-hidden', 'true');
  ui.parentNode.insertBefore(sentinel, ui);

  function absolute(href) {
    if (!href) return null;
    return new URL(href, window.location.origin).href;
  }

  function setStatus(text) {
    if (status) status.textContent = text || '';
  }

  function render() {
    if (!moreBtn) return;
    // Auto-load a few pages, then hand control back to the shopper so the
    // footer stays reachable and the DOM stops growing on its own.
    moreBtn.hidden = !nextUrl || autoLoaded < budget;
    moreBtn.classList.toggle('is-loading', loading);
    moreBtn.disabled = loading;
  }

  /** (Re)read state from the pagination row — on first run and after filtering. */
  function reset() {
    if (controller) {
      controller.abort();
      controller = null;
    }
    loading = false;
    autoLoaded = 0;
    budget = autoPages;
    var link = paginationRow.querySelector('.pagination__next');
    nextUrl = absolute(link && link.getAttribute('href'));
    var prevLink = paginationRow.querySelector('.pagination__prev');
    prevUrl = absolute(prevLink && prevLink.getAttribute('href'));
    paginationRow.classList.toggle('togs-inf-hidden', !!paginationRow.querySelector('.pagination'));
    // Filtering always re-renders from page one, so there is nothing before it.
    if (prevUi && !prevUrl) prevUi.hidden = true;
    setStatus('');
    render();
  }

  /**
   * Load another page if the sentinel is (still) near the viewport and there is
   * auto-load budget left. Called on intersection changes and again after each
   * load, because appending content does not always produce a fresh entry.
   */
  function maybeAutoLoad() {
    if (sentinelVisible && !loading && nextUrl && autoLoaded < budget) {
      loadNext(true);
    }
  }

  function loadNext(isAuto, moveFocus) {
    if (loading || !nextUrl) return;

    var loadedUrl = nextUrl;
    var fetchUrl = loadedUrl;
    if (sectionId) {
      fetchUrl += (fetchUrl.indexOf('?') >= 0 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId);
    }

    loading = true;
    setStatus('Loading more products…');
    render();

    controller = new AbortController();
    fetch(fetchUrl, { method: 'get', signal: controller.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP error! Status: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var template = document.createElement('template');
        template.innerHTML = html;

        var grid = listing.querySelector('.product-grid');
        var newGrid = template.content.querySelector('.product-grid');
        var firstNew = null;

        if (grid && newGrid) {
          var fragment = document.createDocumentFragment();
          while (newGrid.firstElementChild) {
            fragment.appendChild(newGrid.firstElementChild);
          }
          firstNew = fragment.firstElementChild;
          grid.appendChild(fragment);
        }

        // Advance the pointer using the freshly fetched page's own pagination.
        var newNext = template.content.querySelector('.pagination__next');
        nextUrl = absolute(newNext && newNext.getAttribute('href'));

        if (UPDATE_URL) {
          window.history.replaceState({ path: loadedUrl }, '', loadedUrl);
        }

        loading = false;
        controller = null;
        if (isAuto) autoLoaded += 1;

        setStatus(nextUrl ? '' : 'You’ve reached the end.');
        render();

        // Keyboard and screen-reader users who pressed the button land on the
        // first new product rather than being dumped back at the top.
        if (moveFocus && firstNew) {
          firstNew.setAttribute('tabindex', '-1');
          firstNew.focus({ preventScroll: true });
        }

        // The sentinel may still be in view after appending — keep going.
        maybeAutoLoad();
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        loading = false;
        controller = null;
        setStatus('Couldn’t load more products. Please try again.');
        render();
        if (moreBtn) moreBtn.hidden = !nextUrl;
        console.warn(error); // eslint-disable-line no-console
      });
  }

  /**
   * Walk backwards one page, prepending it above the current products.
   * Repeatable, so a shopper who landed on ?page=8 can climb back to page 1.
   */
  function loadPrev() {
    if (loading || !prevUrl) return;

    var fetchUrl = prevUrl;
    if (sectionId) {
      fetchUrl += (fetchUrl.indexOf('?') >= 0 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId);
    }

    loading = true;
    if (prevBtn) {
      prevBtn.classList.add('is-loading');
      prevBtn.disabled = true;
    }
    setStatus('Loading earlier products…');

    controller = new AbortController();
    fetch(fetchUrl, { method: 'get', signal: controller.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP error! Status: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var template = document.createElement('template');
        template.innerHTML = html;

        var grid = listing.querySelector('.product-grid');
        var newGrid = template.content.querySelector('.product-grid');

        if (grid && newGrid) {
          // Prepending pushes everything down, so measure first and scroll by
          // the difference — otherwise the shopper is thrown down the page.
          var heightBefore = document.documentElement.scrollHeight;
          var scrollBefore = window.scrollY;

          var fragment = document.createDocumentFragment();
          while (newGrid.firstElementChild) {
            fragment.appendChild(newGrid.firstElementChild);
          }
          grid.insertBefore(fragment, grid.firstElementChild);

          window.scrollTo(0, scrollBefore + (document.documentElement.scrollHeight - heightBefore));
        }

        var newPrev = template.content.querySelector('.pagination__prev');
        prevUrl = absolute(newPrev && newPrev.getAttribute('href'));

        loading = false;
        controller = null;
        setStatus('');

        if (prevBtn) {
          prevBtn.classList.remove('is-loading');
          prevBtn.disabled = false;
        }
        // Reached page one — the control has nothing left to do.
        if (prevUi && !prevUrl) prevUi.hidden = true;
        render();
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        loading = false;
        controller = null;
        setStatus('Couldn’t load earlier products. Please try again.');
        if (prevBtn) {
          prevBtn.classList.remove('is-loading');
          prevBtn.disabled = false;
        }
        console.warn(error); // eslint-disable-line no-console
      });
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', function (evt) {
      evt.preventDefault();
      loadPrev();
    });
  }

  // Point "Start from the beginning" at this exact view minus the page number,
  // so every active filter survives the trip back to page one.
  if (prevUi) {
    var restart = prevUi.querySelector('.togs-inf__restart');
    if (restart) {
      var startUrl = new URL(window.location.href);
      startUrl.searchParams.delete('page');
      restart.href = startUrl.pathname + startUrl.search;
    }
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      sentinelVisible = entry.isIntersecting;
    });
    maybeAutoLoad();
  }, { rootMargin: PREFETCH_MARGIN });
  observer.observe(sentinel);

  if (moreBtn) {
    moreBtn.addEventListener('click', function (evt) {
      evt.preventDefault();
      // Clicking doesn't fetch a single page — it grants a fresh budget of
      // `clickPages`, so loading goes back to being silent until that runs out.
      // A click that bought only one page reads as a broken button.
      autoLoaded = 0;
      budget = clickPages;
      loadNext(true, true);
    });
  }

  // Symmetry's AJAX filtering replaces the innerHTML of every
  // [data-ajax-container], so start over whenever the grid is swapped out.
  // Our own appends go into .product-grid (a descendant), so they don't fire this.
  new MutationObserver(function () {
    reset();
  }).observe(listing, { childList: true });

  reset();
}());
